import { useState, useMemo } from 'react';
import { type User } from '@/api/entities/users';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { AddUserDialog } from './add-dialog';
import { EditUserDialog } from './edit-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Search, ArrowUp, ArrowDown } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { useUsers } from './api/useUsers';
import { useDeleteUser } from './api/useDeleteUser';
import { formatDateDisplay } from '@/utils/date';
import { TableEmptyState } from '@/components/ui/empty-state';

export function UsersList() {
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const { data, isLoading, isError, error, refetch } = useUsers({
    config: {
      staleTime: 30_000,
    },
  });

  const deleteMutation = useDeleteUser({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'User deleted successfully',
        });
        setDeletingUser(null);
      },
      onError: (err: Error) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to delete user',
          variant: 'destructive',
        });
      },
    },
  });

  // Move useMemo before early returns to follow Rules of Hooks
  const filteredUsers = useMemo(() => {
    const users = (data ?? []) as User[];
    if (!searchQuery.trim()) return users;

    const query = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.name?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query) ||
        user.phone?.toLowerCase().includes(query) ||
        user.type?.toLowerCase().includes(query),
    );
  }, [data, searchQuery]);

  const users = filteredUsers;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        <div className="rounded-lg border p-4">
          <div className="grid grid-cols-3 gap-3">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
          </div>
          <div className="mt-4 space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold tracking-tight">Users</h1>
        <div className="rounded-lg border p-6">
          <p className="text-sm text-destructive">Failed to load users.</p>
          <pre className="mt-2 text-xs text-muted-foreground">{(error as Error)?.message}</pre>
          <Button className="mt-4" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage Waiting List Users</p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>New User</Button>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 max-w-sm">
          <label htmlFor="user-search" className="sr-only">
            Search users
          </label>
          <InputGroup>
            <InputGroupInput
              id="user-search"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search users"
            />
            <InputGroupAddon>
              <Search className="h-4 w-4" aria-hidden="true" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="pl-4">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-12 font-semibold">Ranking</TableHead>
                <TableHead className="h-12 font-semibold">Name</TableHead>
                <TableHead className="h-12 font-semibold">Email</TableHead>
                <TableHead className="h-12 font-semibold">Phone</TableHead>
                <TableHead className="h-12 font-semibold">Type</TableHead>
                <TableHead className="h-12 font-semibold">Joined On</TableHead>
                <TableHead className="h-12 w-[140px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="hover:bg-muted/50">
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">
                        {u.ranking !== undefined && u.ranking !== null ? u.ranking : '-'}
                      </span>
                      {u.ranking !== undefined &&
                        u.ranking !== null &&
                        u.startedRanking !== undefined &&
                        u.startedRanking !== null &&
                        u.ranking !== u.startedRanking && (
                          <div className="flex items-center">
                            {u.ranking < u.startedRanking ? (
                              <span title="Ranking improved">
                                <ArrowUp className="h-4 w-4 text-green-600" />
                              </span>
                            ) : (
                              <span title="Ranking decreased">
                                <ArrowDown className="h-4 w-4 text-red-600" />
                              </span>
                            )}
                          </div>
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium py-4">
                    {u.firstName || u.lastName
                      ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
                      : u.name || '-'}
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">{u.email}</TableCell>
                  <TableCell className="py-4 text-muted-foreground">{u.phone || '-'}</TableCell>
                  <TableCell className="py-4">
                    <span className="capitalize text-muted-foreground">
                      {u.type
                        ? u.type
                            .split('-')
                            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                            .join(' ')
                        : '-'}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">
                    {formatDateDisplay(u.createdAt)}
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingUser(u);
                          setIsEditDialogOpen(true);
                        }}
                        aria-label={`Edit ${u.name || u.email || 'user'}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setDeletingUser(u);
                          setIsDeleteDialogOpen(true);
                        }}
                        aria-label={`Delete ${u.name || u.email || 'user'}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {users.length === 0 && (
                <TableEmptyState
                  title={searchQuery ? 'No users found' : 'No users yet'}
                  description={
                    searchQuery
                      ? 'Try adjusting your search query'
                      : 'Get started by creating your first user'
                  }
                  action={
                    !searchQuery
                      ? {
                          label: 'Add User',
                          onClick: () => setIsAddDialogOpen(true),
                        }
                      : undefined
                  }
                  colSpan={7}
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <AddUserDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      <EditUserDialog
        user={editingUser}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={() => {
          if (deletingUser) {
            deleteMutation.mutate(deletingUser);
          }
        }}
        title="Delete User"
        description={`Are you sure you want to delete "${deletingUser?.email}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
}
